import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { AuthContext } from "../contexts/AuthContext";
import { loginRequest, forgotPasswordRequest } from "../api/auth.api"; 
import { decodeToken } from "../utils/jwt.util";
import './Login.css';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useContext(AuthContext);
    const [credentials, setCredentials] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [sendingReset, setSendingReset] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!credentials.email || !credentials.password) {
            toast.error("Veuillez remplir tous les champs");
            return;
        }

        setLoading(true);

        try {
            const data = await loginRequest(credentials.email, credentials.password);

            console.log("📦 RÉPONSE COMPLÈTE DU BACKEND:", data);

            if (!data?.token) {
                toast.error("Login échoué : pas de token reçu");
                setLoading(false);
                return;
            }

            console.log("✅ Token reçu");

            const decoded = decodeToken(data.token);
            console.log("🔓 CONTENU COMPLET DU TOKEN:", decoded);

            if (!decoded) {
                toast.error("Token invalide");
                setLoading(false);
                return;
            }

            const role = decoded.role || 
                        decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || 
                        decoded.Role ||
                        data.user?.role || // ⬅️ Depuis la réponse du backend
                        "user";
            
            const nom = decoded.name || 
                       decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/name"] ||
                       decoded.given_name ||
                       decoded.unique_name ||
                       decoded.sub ||
                       data.user?.name || 
                       credentials.email.split('@')[0];  
            
            const email = decoded.email || 
                         decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/emailaddress"] ||
                         data.user?.email ||
                         credentials.email;

            const userId = decoded.sub || 
                          decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/nameidentifier"] ||
                          data.user?.id;

            console.log("🎯 DONNÉES EXTRAITES:");
            console.log("   - Nom:", nom);
            console.log("   - Email:", email);
            console.log("   - Rôle:", role);
            console.log("   - ID:", userId);

            localStorage.setItem("token", data.token);
            localStorage.setItem("nom", nom);
            localStorage.setItem("email", email);
            localStorage.setItem("userRole", role);
            localStorage.setItem("userId", userId);
            
            login(data.token);
            
            toast.success(`Bienvenue ${nom} !`);

            const isAdmin = role.toLowerCase() === "admin" || 
                           role.toLowerCase() === "administrator" ||
                           role.toLowerCase().includes("admin");

            console.log("🚀 Redirection:", isAdmin ? "ADMIN" : "USER");

            setTimeout(() => {
                if (isAdmin) {
                    navigate("/admin/dashboard");
                } else {
                    navigate("/");
                }
            }, 1000);

        } catch (err) {
            console.error("❌ Erreur login:", err);
            toast.error(err.message || "Email ou mot de passe incorrect");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();

        if (!resetEmail) {
            toast.error("Veuillez entrer votre email");
            return;
        }

        setSendingReset(true);

        try {
            const response = await forgotPasswordRequest(resetEmail);

            if (response.success) {
                toast.success(response.message || "Email de réinitialisation envoyé ! Vérifiez votre boîte de réception.");
                setShowForgotPassword(false);
                setResetEmail("");
            } else {
                toast.error(response.message || "Erreur lors de l'envoi de l'email");
            }
        } catch (err) {
            console.error("❌ Erreur envoi email:", err);
            toast.error(err.message || "Erreur lors de l'envoi de l'email de réinitialisation");
        } finally {
            setSendingReset(false);
        }
    };

    return (
        <>
            <Toaster position="top-right" richColors />
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <div className="login-icon-wrapper">
                            <svg className="login-svg-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                        </div>
                        <h1 className="login-title">Bienvenue sur notre plateforme</h1>
                        <p className="login-description">Connectez-vous pour accéder à vos services.</p>
                    </div>

                    <div className="login-content">
                        {!showForgotPassword ? (
                            <form onSubmit={handleLogin} className="login-form">
                                <div className="form-group">
                                    <label htmlFor="email" className="form-label">Email</label>
                                    <input
                                        id="email"
                                        type="email"
                                        value={credentials.email}
                                        onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                                        className="form-input"
                                        placeholder="votre@email.com"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="password" className="form-label">Mot de passe</label>
                                    <div className="password-wrapper">
                                        <input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            value={credentials.password}
                                            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                                            className="form-input password-input"
                                            placeholder="••••••••"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="password-toggle-btn"
                                        >
                                            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </button>
                                    </div>
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={loading} 
                                    className="submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Connexion...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-sign-in-alt"></i>
                                            Se connecter
                                        </>
                                    )}
                                </button>

                                <div className="login-footer">
                                    <p className="footer-text">
                                        Vous n'avez pas de compte ?{" "}
                                        <Link to="/register" className="register-link">
                                            S'inscrire
                                        </Link>
                                        {" | "}
                                        <button 
                                            type="button"
                                            onClick={() => setShowForgotPassword(true)}
                                            className="register-link"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                        >
                                            Mot de passe oublié ?
                                        </button>
                                    </p>
                                    <Link to="/" className="home-link">
                                        <i className="fas fa-home"></i>
                                        Retour à l'accueil
                                    </Link>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleForgotPassword} className="login-form">
                                <div className="form-group">
                                    <label htmlFor="reset-email" className="form-label">Email de récupération</label>
                                    <input
                                        id="reset-email"
                                        type="email"
                                        value={resetEmail}
                                        onChange={(e) => setResetEmail(e.target.value)}
                                        className="form-input"
                                        placeholder="votre@email.com"
                                        required
                                    />
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={sendingReset} 
                                    className="submit-btn"
                                >
                                    {sendingReset ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane"></i>
                                            Envoyer l'email de réinitialisation
                                        </>
                                    )}
                                </button>

                                <div className="login-footer">
                                    <button 
                                        type="button"
                                        onClick={() => setShowForgotPassword(false)}
                                        className="register-link"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                    >
                                        ← Retour à la connexion
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;